import { useCallback, useEffect, useState } from "react";
import { Box, Button, Card, CardActionArea, CardContent, Grid, LinearProgress, Typography, alpha } from "@mui/material";
import type { Dashboard, PlanEntitlement } from "@ebook/contracts";
import { useNavigate } from "@tanstack/react-router";
import AddRounded from "@mui/icons-material/AddRounded";
import AutoStoriesRounded from "@mui/icons-material/AutoStoriesRounded";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import AutorenewRounded from "@mui/icons-material/AutorenewRounded";
import ErrorRounded from "@mui/icons-material/ErrorRounded";
import EditNoteRounded from "@mui/icons-material/EditNoteRounded";
import StorageRounded from "@mui/icons-material/StorageRounded";
import SettingsRounded from "@mui/icons-material/SettingsRounded";
import WorkspacePremiumRounded from "@mui/icons-material/WorkspacePremiumRounded";
import { api } from "../lib/api-client";
import { PageError, PageLoading } from "../components/PageState";
import { PlanUpgradeDialog } from "../components/PlanUpgradeDialog";
import { planById } from "../lib/plans";
import { useTranslate } from "../i18n";

const PROCESSING_STATUSES = ["uploading", "queued", "processing"];

function formatMegabytes(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(bytes >= 10 * 1024 * 1024 ? 1 : 2)} MB`;
}

export function DashboardPage() {
  const [data, setData] = useState<Dashboard | null>(null);
  const [entitlement, setEntitlement] = useState<PlanEntitlement | null>(null);
  const [error, setError] = useState("");
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const navigate = useNavigate();
  const __ = useTranslate();

  const load = useCallback(async () => {
    setError("");
    try {
      const [dashboard, quota] = await Promise.all([api.dashboard(), api.entitlement()]);
      setData(dashboard);
      setEntitlement(quota);
    } catch (caught) { setError(caught instanceof Error ? caught.message : __("โหลด Dashboard ไม่สำเร็จ")); }
  }, [__]);
  useEffect(() => { void load(); }, [load]);
  if (error) return <PageError message={error} onRetry={() => void load()} />;
  if (!data) return <PageLoading />;

  const draft = Math.max(0, data.total - data.ready - data.processing - data.failed);
  const categories: Array<{
    key: string; label: string; value: number; color: "primary" | "success" | "warning" | "error" | "neutral";
    icon: typeof AutoStoriesRounded; status?: string; filterLabel?: string;
  }> = [
    { key: "total", label: __("ทั้งหมด"), value: data.total, color: "primary", icon: AutoStoriesRounded },
    { key: "ready", label: __("พร้อมใช้งาน"), value: data.ready, color: "success", icon: CheckCircleRounded, status: "ready" },
    { key: "processing", label: __("กำลังประมวลผล"), value: data.processing, color: "warning", icon: AutorenewRounded, status: PROCESSING_STATUSES.join(","), filterLabel: __("กำลังประมวลผล") },
    { key: "failed", label: __("มีปัญหา"), value: data.failed, color: "error", icon: ErrorRounded, status: "failed" },
    { key: "draft", label: __("แบบร่าง"), value: draft, color: "neutral", icon: EditNoteRounded, status: "draft" },
  ];

  const plan = planById(entitlement?.effectivePlan);

  return <Box>
    <Typography variant="h4" fontWeight={800} mb={3}>{__("Dashboard")}</Typography>

    <Grid container spacing={2.5}>
      {categories.map(({ key, label, value, color, icon: Icon, status, filterLabel }) => {
        const fg = color === "neutral" ? "text.secondary" : `${color}.main`;
        return <Grid key={key} size={{ xs: 6, sm: 4, md: 2.4 }}>
          <Card
            variant="outlined"
            sx={{
              position: "relative",
              overflow: "hidden",
              transition: "border-color .15s ease",
              "&:hover": { borderColor: fg },
              "&::before": {
                content: '""',
                position: "absolute",
                insetInline: 0,
                top: 0,
                height: 3,
                bgcolor: fg,
                zIndex: 1,
              },
            }}
          >
            <CardActionArea onClick={() => void navigate({ to: "/ebooks", search: { status, label: filterLabel } })}>
              <CardContent>
                <Box
                  sx={{
                    display: "grid",
                    placeItems: "center",
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    mb: 1.5,
                    color: fg,
                    bgcolor: (theme) => alpha(
                      color === "neutral" ? theme.palette.text.secondary : theme.palette[color].main,
                      theme.palette.mode === "dark" ? 0.18 : 0.1,
                    ),
                  }}
                >
                  <Icon fontSize="small" />
                </Box>
                <Typography color="text.secondary" variant="body2">{label}</Typography>
                <Typography variant="h3" fontWeight={800} mt={0.5}>{value}</Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>;
      })}
    </Grid>

    <Grid container spacing={2.5} mt={3}>
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <Card variant="outlined" sx={{ height: "100%" }}>
          <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.5, height: "100%" }}>
            <Box display="flex" alignItems="center" gap={1}>
              <WorkspacePremiumRounded fontSize="small" color="primary" />
              <Typography variant="subtitle2" fontWeight={800}>{plan?.name ?? __("ยังไม่เลือกแพ็กเกจ")}</Typography>
            </Box>
            {entitlement && <>
              <Box display="flex" justifyContent="space-between" alignItems="baseline">
                <Typography variant="body2" color="text.secondary">{__("Ebook ที่ใช้ไป")}</Typography>
                <Typography variant="body2" fontWeight={700}>{entitlement.booksUsed} / {entitlement.booksLimit ?? "∞"}</Typography>
              </Box>
              {entitlement.booksLimit !== null && <LinearProgress
                variant="determinate"
                value={Math.min(100, (entitlement.booksUsed / entitlement.booksLimit) * 100)}
                sx={{ height: 6, borderRadius: 999 }}
              />}
              {entitlement.expiresAt && <Typography variant="caption" color="text.secondary">
                {__("ต่ออายุถัดไป {{date}}", { date: new Date(entitlement.expiresAt).toLocaleDateString("th-TH") })}
              </Typography>}
            </>}
            <Box flex={1} />
            <Button variant="contained" size="small" onClick={() => setUpgradeOpen(true)}>{__("อัปเกรดแพ็กเกจ")}</Button>
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <Card variant="outlined" sx={{ height: "100%" }}>
          <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.5, height: "100%" }}>
            <Box display="flex" alignItems="center" gap={1}>
              <StorageRounded fontSize="small" color="info" />
              <Typography variant="subtitle2" fontWeight={800}>{__("พื้นที่จัดเก็บ")}</Typography>
            </Box>
            <Typography variant="h5" fontWeight={800}>{formatMegabytes(data.storageBytes)}</Typography>
            <Typography variant="caption" color="text.secondary">{__("รวมทุก Ebook ของคุณ")}</Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12, sm: 12, md: 4 }}>
        <Card variant="outlined" sx={{ height: "100%" }}>
          <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.5, height: "100%" }}>
            <Typography variant="subtitle2" fontWeight={800}>{__("ทางลัด")}</Typography>
            <Button fullWidth variant="contained" startIcon={<AddRounded />} onClick={() => void navigate({ to: "/ebooks" })}>
              {__("อัปโหลด PDF ใหม่")}
            </Button>
            <Button fullWidth variant="outlined" startIcon={<SettingsRounded />} onClick={() => void navigate({ to: "/settings" })}>
              {__("ตั้งค่า")}
            </Button>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
    <PlanUpgradeDialog open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />
  </Box>;
}
